import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ExternalLink, Pin, X } from "lucide-react";
import { useW } from "./ctx";
import { FLAG, ORIGIN, docLabel, docOf, lineStatus, money } from "./lib";
import { lineProduct } from "./Submittal";

/* A highlight marks a passage; it never changes the document. Hover (or focus) shows its popover, click pins it. */
export function Hl({ id, cls = "hl", children, as: Tag = "span", className = "" }) {
  const w = useW();
  const ref = useRef(null);
  const open = w.pop?.id === id && w.pop?.el === ref.current;
  return (
    <Tag ref={ref} data-src={id} tabIndex={0} role="button" aria-label="Show source details"
      className={`${cls} ${open ? "is-open" : ""} ${className}`}
      onMouseEnter={() => w.hoverPop({ kind: "source", id, el: ref.current })} onMouseLeave={w.leavePop}
      onFocus={() => w.hoverPop({ kind: "source", id, el: ref.current })} onBlur={w.leavePop}
      onClick={(e) => { e.stopPropagation(); w.pinPop({ kind: "source", id, el: ref.current }); }}
      onKeyDown={(e) => { if (e.key === "Enter") w.pinPop({ kind: "source", id, el: ref.current }); }}>
      {children}
    </Tag>
  );
}

// The class a highlight gets: flagged passages tint by flag color, codes purple, catalog green, the rest yellow.
export function hlClass(w, id) {
  const s = w.data?.sources?.[id];
  const f = w.flagBySource[id];
  if (f) return f.kind === "confirm" || f.kind === "assumption" ? "hl hl-amber" : "hl hl-flag";
  if (s?.origin === "code") return "hl hl-code";
  if (s?.origin === "catalog") return "hl hl-cat";
  return "hl";
}

export function SourceTag({ id, line }) {
  const w = useW();
  const s = w.data?.sources?.[id];
  if (!s) return null;
  return (
    <button className={`tag ${ORIGIN[s.origin]?.tag || ""}`} title={`${ORIGIN[s.origin]?.label} · ${s.label}`}
      onClick={(e) => { e.stopPropagation(); w.openSource(id, line); }} data-testid={`tag-${id}`}>
      {short(s, w)}
    </button>
  );
}

const short = (s) => {
  if (s.origin === "code") return s.label;
  if (s.origin === "catalog") return s.label.replace(/\.pdf$/, "").split(" ").slice(0, 3).join(" ");
  if (s.id.startsWith("A601-")) return `${s.label}${s.field ? ` · ${s.field.replace(/_in$|_min$/, "").replace(/_/g, " ")}` : ""}`;
  if (s.id.startsWith("SEC-")) return s.label;
  if (s.id.startsWith("OS-")) return `Spec p. ${s.page}${s.field ? ` · ${s.field.replace(/_in$|_min$/, "").replace(/_/g, " ")}` : ""}`;
  return s.label;
};

export function Popover() {
  const w = useW();
  const p = w.pop;
  const box = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!p?.el) { setPos(null); return undefined; }
    const place = () => {
      if (!p.el.isConnected) { w.closePop(); return; }
      const r = p.el.getBoundingClientRect();
      const h = box.current?.offsetHeight || 260;
      const width = Math.min(380, window.innerWidth - 24);
      const below = r.bottom + h + 16 < window.innerHeight || r.top < h + 16;
      const left = Math.max(12, Math.min(r.left, window.innerWidth - width - 12));
      setPos({ left, top: below ? r.bottom + 10 : r.top - h - 10, side: below ? "below" : "above",
        ax: Math.max(14, Math.min(width - 26, r.left + Math.min(r.width, 40) / 2 - left)) });
    };
    place();
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => { window.removeEventListener("scroll", place, true); window.removeEventListener("resize", place); };
  }, [p, w]);

  useEffect(() => {
    if (!p?.pinned) return undefined;
    const esc = (e) => e.key === "Escape" && w.closePop();
    const outside = (e) => { if (!box.current?.contains(e.target) && !p.el?.contains(e.target)) w.closePop(); };
    window.addEventListener("keydown", esc);
    window.addEventListener("mousedown", outside);
    return () => { window.removeEventListener("keydown", esc); window.removeEventListener("mousedown", outside); };
  }, [p, w]);

  if (!p) return null;
  return (
    <div ref={box} className="cn cn-pop fade-up" data-side={pos?.side || "below"} role="dialog" data-testid="popover"
      style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, "--ax": `${pos?.ax ?? 24}px` }}
      onMouseEnter={w.keepPop} onMouseLeave={w.leavePop}>
      <div className="scroll max-h-[min(460px,60vh)] p-4">
        <div className="mb-2 flex items-center justify-end gap-1">
          {p.pinned ? (
            <button className="icon-btn" onClick={w.closePop} aria-label="Close" data-testid="popover-close"><X size={14} /></button>
          ) : <span className="flex items-center gap-1 text-[11px] text-[var(--muted)]"><Pin size={11} /> Click to pin</span>}
        </div>
        {p.kind === "line" ? <LineBody id={p.id} /> : <SourceBody id={p.id} />}
      </div>
    </div>
  );
}

function SourceBody({ id }) {
  const w = useW();
  const s = w.data?.sources?.[id] || w.data?.sources?.[id.split("#")[0]];
  if (!s) return <p>Source not found.</p>;
  const lines = w.drives[s.id] || [];
  const f = w.flagBySource[s.id];
  const o = ORIGIN[s.origin];
  return (
    <div className="-mt-8 space-y-3">
      <span className={`badge ${o.cls}`}>{o.label}</span>
      <div>
        <p className="font-semibold leading-snug">{s.label}{s.title ? ` · ${s.title}` : ""}</p>
        {s.edition && <p className="text-[12px] text-[var(--muted)]">{s.edition}</p>}
      </div>
      <p className="text-[13px] leading-relaxed text-[var(--ink2)]">{s.meaning || meaningFallback(s)}</p>
      {f && <FlagMini f={f} />}
      <LinesList lines={lines} />
    </div>
  );
}

const meaningFallback = (s) => (s.text ? `“${s.text.length > 260 ? `${s.text.slice(0, 260)}…` : s.text}”` : "");

function FlagMini({ f }) {
  const w = useW();
  const st = lineStatus(null, f, w.project?.decisions);
  return (
    <div className="rounded-lg border border-[var(--line)] bg-[var(--bg)] p-3">
      <div className="flex items-center gap-2"><span className={`badge ${FLAG[f.kind].cls}`}>{FLAG[f.kind].label}</span>
        <span className={`badge ${st.cls}`}>{st.label}</span></div>
      <p className="mt-2 text-[13px] font-semibold leading-snug">{f.explanation.heading}</p>
      <p className="mt-1 text-[12.5px] text-[var(--ink2)]"><b>Condoor submitted:</b> {f.explanation.submitted}</p>
      <button className="mt-2 text-[12.5px] font-medium text-[var(--accent-ink)] underline" onClick={() => w.openLine(f.line, "quick")}>
        See the full explanation on line {f.line}</button>
    </div>
  );
}

function LinesList({ lines }) {
  const w = useW();
  if (!lines.length) return <p className="text-[12.5px] text-[var(--muted)]">Drives no line items directly.</p>;
  const shown = lines.slice(0, 6);
  return (
    <div>
      <p className="panel-title mb-1.5">Drives {lines.length} line item{lines.length > 1 ? "s" : ""}</p>
      <div className="space-y-1">
        {shown.map((l) => (
          <div key={l.id} className="flex items-center gap-2 text-[12.5px]">
            <span className="mono w-[64px] shrink-0">{l.id}</span>
            <span className="min-w-0 flex-1 truncate">{l.item} · {l.cat}</span>
            <button className="btn btn-ghost btn-sm h-6 px-2" onClick={() => w.openLine(l.id, "doc")}>In submittal</button>
            <button className="btn btn-ghost btn-sm h-6 px-2" onClick={() => w.openLine(l.id, "quick")}>In line items</button>
          </div>
        ))}
        {lines.length > shown.length && <p className="text-[12px] text-[var(--muted)]">and {lines.length - shown.length} more</p>}
      </div>
    </div>
  );
}

function LineBody({ id }) {
  const w = useW();
  const l = w.lineById[id];
  if (!l) return null;
  const { f, chat, ed } = lineProduct(w, l);
  const item = w.itemByCode[ed?.to || l.cat];
  const cut = l.cut && w.data.cut_sheets[l.cut];
  const st = chat ? { label: `Edited by ${chat.by}`, cls: "b-edited" } : lineStatus(l, f, w.project?.decisions);
  return (
    <div className="-mt-8 space-y-3">
      <span className="badge b-gen">Generated by Condoor</span>
      <div>
        <p className="mono text-[var(--muted)]">{l.id} · qty {l.qty}</p>
        <p className="font-semibold leading-snug">{l.item}: {ed ? ed.product : l.product}</p>
        <p className="mono text-[var(--muted)]">{ed ? <><s>{l.cat}</s> {ed.to}</> : l.cat}{l.hand ? ` · ${l.hand}` : ""}</p>
        <span className={`badge mt-1 ${st.cls}`}>{st.label}</span>
      </div>
      {f && <p className="text-[12.5px] text-[var(--ink2)]"><b>{f.explanation.heading}.</b> {f.explanation.submitted}</p>}
      <div>
        <p className="panel-title mb-1.5">Sources</p>
        <div className="flex flex-wrap gap-1">{l.src.filter((s) => w.data.sources[s]?.origin !== "catalog").map((s) => <SourceTag key={s} id={s} line={l.id} />)}</div>
      </div>
      {item && (
        <div className="rounded-lg border border-[#bfe3cb] bg-[var(--cat-bg)] p-3 text-[12.5px]">
          <p className="panel-title !text-[var(--cat)]">{w.role === "supplier" ? "Catalog item · supplier only" : "Product"}</p>
          <p className="mt-1 font-semibold">{item.code} · {item.item}</p>
          <p className="text-[var(--ink2)]">{item.mfr} · {item.finish} · {item.label}</p>
          {w.role === "supplier" && <p className="mt-1">{money(item.price)} · {item.stock} in stock · {item.lead} · {item.warranty}</p>}
        </div>
      )}
      {cut && (
        <button className="flex w-full items-center gap-2 rounded-lg border border-[var(--line)] p-2.5 text-left text-[12.5px] hover:bg-[var(--bg)]"
          onClick={() => w.openDoc(`cut:${l.cut}`, "doc")}>
          <ExternalLink size={13} /> <span className="truncate">{cut.file}</span><span className="ml-auto text-[var(--muted)]">{cut.docType}</span>
        </button>
      )}
    </div>
  );
}

export const docForSource = docOf;
export const labelForDoc = docLabel;
